import type Store from '@ember-data/store';
import { concat, hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { LinkTo } from '@ember/routing';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import repeat from '@nullvoxpopuli/ember-composable-helpers/helpers/repeat';
import can from 'ember-can/helpers/can';
import formatDate from 'ember-intl/helpers/format-date';
import t from 'ember-intl/helpers/t';
import eq from 'ember-truth-helpers/helpers/eq';
import ArchiveJournalEventButton from 'parog-web/components/archive-journal-event-button';
import PreferredUnitCachePendingMessage from 'parog-web/components/bio-exam-results/preferred-unit-cache-pending-message';
import Row from 'parog-web/components/bio-exam-results/summary/row';
import FileAttachmentsCell from 'parog-web/components/bio-exam-results/summary/row/file-attachments-cell';
import Title from 'parog-web/components/bio-exam-results/summary/title';
import bioExamResultsResultsSource from 'parog-web/helpers/bio-exam-results/results-source';
import capitalize from 'parog-web/helpers/capitalize';
import incrementalList from 'parog-web/helpers/incremental-list';
import type BioExamResultsReceivedJE from 'parog-web/models/bio-exam-results-received-journal-event';
import type BioExamObservation from 'parog-web/models/loinc';
import type { Specimen } from 'parog-web/models/loinc';
import type Patient from 'parog-web/models/patient';
import type SemeiaSessionService from 'parog-web/services/session';
import { sortBy } from 'parog-web/utils/sort';
import triggerFileDownload from 'parog-web/utils/trigger-file-download';
import trimLoincCode from 'parog-web/utils/trim-loinc-code';
import type { S as RowS } from './summary/row';
import { CellBioExamResult, referencesHookClass } from './summary/row/cell';

type CategoryCode = Specimen | 'MR';

interface Args extends Pick<RowS['Args'], 'activeReferencesHook'> {
  isLoading?: unknown;
  hideAction?: unknown;
  patient: Patient;
}

interface S {
  Args: Args;
}

class Category {
  get label() {
    if (this.code == 'BLD') return `bioExamResults.blood`;
    if (this.code == 'UR') return 'bioExamResults.urinary';
    if (this.code == 'MR') return 'bioExamResults.main-results';

    return 'bioExamResults.other';
  }

  constructor(public code: CategoryCode) {}
}

class Chapter {
  constructor(public label: string) {}
}

class LoincWithResults {
  get label() {
    return this.loinc.loinc;
  }

  constructor(
    public loinc: BioExamObservation,
    public results: CellBioExamResult[],
    public isImportantRow?: boolean
  ) {}
}

type LoincRow = Category | Chapter | LoincWithResults;

export default class BioExamResultsSummaryComponent extends Component<S> {
  @service declare router: RouterService;
  @service declare session: SemeiaSessionService;
  @service declare store: Store;

  @tracked activeReferencesHook?: string;

  incrementalList = incrementalList;

  @cached
  get bioExamResultsReceived(): BioExamResultsReceivedJE[] {
    return (
      this.args.patient.bioExamResultsReceivedJournalEvents.sort(
        sortBy('occurredAt', 'desc')
      ) ?? []
    );
  }

  @cached
  get loincRows(): LoincRow[] {
    const result: LoincRow[] = [];

    const seenCategories = new Set<CategoryCode>();
    const seenChapters = new Set<string>();

    if (this.#shouldShowImportantSection) {
      this.args.patient.priorityBioExamLoincs.forEach(loinc => {
        const observation = this.store.peekRecord('loinc', loinc);
        if (!observation) return;
        const loincWithResult = this.#getLoincWithResult(observation);
        if (loincWithResult) {
          loincWithResult.isImportantRow = true;
          result.push(loincWithResult);
        }
      });
      if (result.length) {
        result.unshift(new Category('MR'));
      }
    }

    this.#sortedLoinc().forEach(observation => {
      const { specimen: category, loincChapterLabel } = observation;

      const loincWithResult = this.#getLoincWithResult(observation);

      // If there are not results matching this LOINC, early return
      if (!loincWithResult) return;

      if (category && !seenCategories.has(category)) {
        seenCategories.add(category);
        result.push(new Category(category));
      }

      const chapterId = `${category ?? 'other'}-${loincChapterLabel}`;
      if (loincChapterLabel && !seenChapters.has(chapterId)) {
        seenChapters.add(chapterId);
        result.push(new Chapter(loincChapterLabel));
      }

      result.push(loincWithResult);
    });

    return result;
  }

  get #shouldShowImportantSection(): boolean {
    if (!this.session.medic) return false;

    return this.session.medic.seesImportantBioResultsSection;
  }

  commentsOnNotifications(bioExamResults): number {
    return bioExamResults.notificationTriggeredJournalEvents.reduce(
      (total, notif) => {
        return total + (notif.commentWrittenJournalEvents?.length ?? 0);
      },
      0
    );
  }

  downloadSummary = () => {
    const dates = [''];
    const rows: string[][] = [];

    document
      .querySelectorAll(
        '#bio-summary-table > thead > tr > th[data-test-column]'
      )
      .forEach(thElement => {
        dates.push(
          (thElement as HTMLTableCellElement).innerText.split('\n')[0]
        );
      });

    rows.push(dates);

    let group = '';

    document.querySelectorAll('table > tbody > tr').forEach(trElement => {
      const rowContent: string[] = [];
      // Get the LOINC group value from the tr element
      if (trElement.attributes.getNamedItem('data-test-group')) {
        group = (trElement.attributes.getNamedItem('data-test-group') as Attr)
          .value;
      }
      // Get the LOINC chapter from the tr element
      if (trElement.attributes.getNamedItem('data-test-loinc-chapter-row')) {
        let loincChapter = (
          trElement.children[0] as HTMLTableCellElement
        ).innerText.trim();

        // Append the LOINC group to the LOINC Chapter
        switch (group) {
          // Ex : Hématologie sanguine
          case 'blood':
            loincChapter += ' sanguine';
            break;
          // Ex : Biochimie urinaire
          case 'urinary':
            loincChapter += ' urinaire';
            break;
          // Ex : Autres réultats - Fertilité
          default:
            loincChapter = `Autres résultats - ${loincChapter}`;
        }
        rowContent.push(loincChapter);
      }
      // Get the result value & unit from the tr element
      if (trElement.attributes.getNamedItem('data-test-results-row')) {
        const children = trElement.children;
        for (let i = 0; i < children.length; i++) {
          const child = children.item(i) as HTMLTableCellElement;
          rowContent.push(child.innerText.replace('\n', ' ').trim());
        }
      }

      if (rowContent.length) rows.push(rowContent);
    });

    triggerFileDownload({
      fileContent:
        'data:text/csv;charset=utf-8,' + rows.map(e => e.join(',')).join('\n'),
      fileName: `historique_bilans_biologie_patient_${this.args.patient.fullName.replace(
        ' ',
        '_'
      )}.csv`
    });
  };

  isCategory = (item: LoincRow): item is Category => {
    return item instanceof Category;
  };

  isChapter = (item: LoincRow): item is Chapter => {
    return item instanceof Chapter;
  };

  isLoincWithResults = (item: LoincRow): item is LoincWithResults => {
    return !this.isCategory(item) && !this.isChapter(item);
  };

  showReferences = (event: Event) => {
    this.activeReferencesHook = (event.target as HTMLElement).closest(
      `.${referencesHookClass}`
    )?.id;
  };

  #getLoincWithResult(
    observation: BioExamObservation
  ): LoincWithResults | null {
    /*
      Get all the results for a given LOINC.
      Each result will be associated to the JE it comes from
      */
    const resultsPerLoinc = this.bioExamResultsReceived.map(je => {
      const result = je.displayableBioExamResults.find(
        ({ code }) => trimLoincCode(code) === observation.loinc
      );
      return new CellBioExamResult(result, je);
    });

    // If there are not results matching this LOINC, return null
    if (
      resultsPerLoinc.filter(cellResult => Boolean(cellResult.result)).length ==
      0
    )
      return null;

    return new LoincWithResults(observation, resultsPerLoinc);
  }

  #sortedLoinc() {
    const specimenPriority = (specimen: BioExamObservation['specimen']) => {
      if (specimen == 'BLD') return 0;
      if (specimen == 'UR') return 1;

      // all the other values
      return 2;
    };

    // @ts-expect-error the return type of this.store.peekAll provided by @types/ember-data is not correct
    const loincs = this.store.peekAll('loinc') as BioExamObservation[];

    return loincs.toSorted(function (bioA, bioB) {
      const specimenA = specimenPriority(bioA.specimen);
      const specimenB = specimenPriority(bioB.specimen);

      // Sort first by specimen, then by rank
      return specimenA !== specimenB
        ? specimenA - specimenB
        : bioA.rank - bioB.rank;
    });
  }

  <template>
    {{#if @patient.hasPreferredUnitCachePending}}
      <PreferredUnitCachePendingMessage />
    {{/if}}
    <div class='my-[1em] flex justify-between [&_i]:cursor-pointer'>
      {{#unless @hideAction}}
        {{#if this.session.medic}}
          <LinkTo
            @route='medics-web.protected.patients.patient.bio-results.edition'
            @model={{@patient}}
            @query={{hash sourceJeId=null}}
            data-test-new-measure='bio-exam'
            class='block'
          >
            <i class='ui add icon'></i>
            Ajouter un bilan de biologie
          </LinkTo>
        {{else}}
          <LinkTo
            @route='patient-web.protected.bio-results.edition'
            @query={{hash sourceJeId=null}}
            data-test-new-measure='bio-exam'
            class='block'
          >
            <i class='ui add icon'></i>
            Ajouter un bilan de biologie
          </LinkTo>
        {{/if}}
      {{/unless}}

      {{#if @isLoading}}
        <div class='ui mini active loader inline'></div>
      {{/if}}

      {{#if this.session.medic}}
        <i
          role='button'
          data-test-download
          class='icon large download semeia primary-color'
          {{on 'click' this.downloadSummary}}
        ></i>
      {{/if}}
    </div>

    {{#if @isLoading}}
      <div class='max-h-[80vh] overflow-scroll [scrollbar-width:thin]'>
        <table
          class='ui striped celled definition unstackable table [&.ui.celled.table>tbody>tr>td]:[border-left-style:none] [&.ui.celled.table>tbody>tr>td]:[border-right:1px_solid_rgba(34,36,38,0.1)] [&.ui.celled.table>thead>tr>th]:[border-left-style:none] [&.ui.celled.table>thead>tr>th]:[border-right:1px_solid_rgba(34,36,38,0.1)] [&.ui.definition.table>tbody>tr>td:first-child]:[border-left:1px_solid_rgba(34,36,38,0.1)] [&.ui.definition.table>tbody>tr>td:nth-child(2)]:[border-left-style:none] [&.ui.definition.table>thead:not(.full-width)>tr>th:nth-child(2)]:[border-left-style:none] [&.ui.table]:[border-left-style:none] [&.ui.table]:[border-right-style:none]'
        >
          <thead>
            <tr>
              <th class='sticky max-sm:left-0 max-sm:top-0 max-sm:z-[1]'></th>
              <th></th>
              <th></th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {{#each (repeat 3)}}
              <tr>
                <td></td>
                {{#each (repeat 3)}}
                  <td><div class='ui placeholder'><div class='paragraph'><div
                          class='line'
                        ></div></div></div></td>
                {{/each}}
              </tr>
            {{/each}}
          </tbody>
        </table>
      </div>
    {{else}}
      {{#if this.bioExamResultsReceived}}
        <div class='max-h-[80vh] overflow-scroll [scrollbar-width:thin]'>
          <table
            {{!
        We use the "mouseover" event here for ⚡ perf reasons

        We want to use only 1 event listener, but need to know what cell the mouse is currently hovering
        The "mouseover" allows that since it bubbles, while "mouseenter" does not.

        "mousemove" would also work but would be overkill since it would trigger for every move, not just element node changes
        }}
            {{on 'mouseover' this.showReferences}}
            {{! Mark this table as interactive since it has tooltips on its cells }}
            tabindex='-1'
            data-test-bio-exam-results-summary
            class='ui striped celled definition unstackable table [&.ui.celled.table>tbody>tr>td]:[border-left-style:none] [&.ui.celled.table>tbody>tr>td]:[border-right:1px_solid_rgba(34,36,38,0.1)] [&.ui.celled.table>thead>tr>th]:[border-left-style:none] [&.ui.celled.table>thead>tr>th]:[border-right:1px_solid_rgba(34,36,38,0.1)] [&.ui.definition.table>tbody>tr>td:first-child]:[border-left:1px_solid_rgba(34,36,38,0.1)] [&.ui.definition.table>tbody>tr>td:nth-child(2)]:[border-left-style:none] [&.ui.definition.table>thead:not(.full-width)>tr>th:nth-child(2)]:[border-left-style:none] [&.ui.table]:[border-left-style:none] [&.ui.table]:[border-right-style:none]'
            id='bio-summary-table'
          >
            <thead class='sticky top-0 z-[1]'>
              <tr>
                <th
                  class='min-w-[320px] sm:sticky sm:left-0 sm:top-0 sm:z-[1]'
                ></th>
                {{#each this.bioExamResultsReceived as |bioExamResults|}}
                  <th
                    class='center aligned min-w-[130px]'
                    data-test-column={{bioExamResults.id}}
                  >
                    {{capitalize
                      (formatDate bioExamResults.occurredAt format='DD/MM/YYYY')
                    }}

                    <div
                      data-test-results-source
                      class='text-[0.9em] font-[400]'
                    >
                      {{bioExamResultsResultsSource bioExamResults}}
                      {{~#if bioExamResults.prescriptor}}, Prescrit par
                        {{bioExamResults.prescriptor}}
                      {{/if}}
                    </div>

                    {{#if bioExamResults.showCorrection}}
                      <div data-test-correction>
                        <span class='placeholder-label'>
                          {{t 'bioExamResultCodes.showCorrection'}}
                        </span>
                      </div>
                    {{/if}}

                    {{#if bioExamResults.canEdit}}
                      {{#if this.session.medic}}
                        <LinkTo
                          @route='medics-web.protected.patients.patient.bio-results.edition'
                          @model={{@patient}}
                          @query={{hash sourceJeId=bioExamResults.id}}
                          data-test-edit-bio-exam-results-link={{bioExamResults.id}}
                        >
                          <i class='pencil alternate icon'></i>
                        </LinkTo>
                      {{else}}
                        <LinkTo
                          @route='patient-web.protected.bio-results.edition'
                          @query={{hash sourceJeId=bioExamResults.id}}
                          data-test-edit-bio-exam-results-link={{bioExamResults.id}}
                        >
                          <i class='pencil alternate icon'></i>
                        </LinkTo>
                      {{/if}}
                    {{/if}}

                    {{#if (can 'archive form' 'bio-exam-results/edition-form')}}
                      <ArchiveJournalEventButton
                        @journalEvent={{bioExamResults}}
                        @additionalInformation={{concat
                          'Cette opération entraînera la suppression de tous les résultats du '
                          (formatDate
                            bioExamResults.occurredAt format='DD MMMM YYYY'
                          )
                        }}
                        class='ui tertiary fitted primary button'
                      />
                    {{/if}}
                  </th>
                {{/each}}
              </tr>
            </thead>
            <tbody>
              <tr data-test-files-row>
                <td
                  class='whitespace-nowrap sm:sticky sm:left-0 sm:!bg-[#f7f7f7]'
                >
                  Fichiers déclarés
                </td>
                {{#each this.bioExamResultsReceived as |bioExamResults|}}
                  <td
                    class='center aligned'
                    data-test-column={{bioExamResults.id}}
                  >
                    <FileAttachmentsCell
                      @journalEvent={{bioExamResults}}
                      @patient={{@patient}}
                    />
                  </td>

                {{/each}}
              </tr>
              {{#if this.session.medic}}
                <tr data-test-files-row>
                  <td
                    class='whitespace-nowrap sm:sticky sm:left-0 sm:!bg-[#f7f7f7]'
                  >
                    Commentaires sur les bilans
                  </td>
                  {{#each this.bioExamResultsReceived as |bioExamResults|}}
                    <td
                      class='center aligned'
                      data-test-comments-column={{bioExamResults.id}}
                    >
                      <LinkTo
                        @route='medics-web.protected.patients.patient.bio-results.edition'
                        @model={{@patient}}
                        @query={{hash sourceJeId=bioExamResults.id}}
                      >
                        <div class='flex items-center justify-center'>
                          {{#let
                            (this.commentsOnNotifications bioExamResults)
                            as |commentCount|
                          }}
                            {{#if commentCount}}
                              {{commentCount}}
                              {{if
                                (eq commentCount 1)
                                'commentaire'
                                'commentaires'
                              }}
                            {{else}}
                              —
                            {{/if}}
                          {{/let}}
                        </div>
                      </LinkTo>
                    </td>

                  {{/each}}
                </tr>
              {{/if}}
              {{! Specifying the "key" for the "each" loop to make sure we do not re-render the entire DOM when a bio JE is removed }}
              {{#each
                (this.incrementalList this.loincRows 5) key='label'
                as |item|
              }}
                {{#if (this.isLoincWithResults item)}}
                  <Row
                    @patient={{@patient}}
                    @code={{item.loinc.loinc}}
                    @results={{item.results}}
                    @activeReferencesHook={{this.activeReferencesHook}}
                    @isImportantRow={{item.isImportantRow}}
                  />
                {{else}}
                  <Title
                    @name={{if
                      (this.isCategory item)
                      (t item.label)
                      item.label
                    }}
                    @isH2={{this.isCategory item}}
                    data-test-group={{if
                      (this.isCategory item)
                      item.label
                      false
                    }}
                    data-test-loinc-chapter-row={{this.isChapter item}}
                  />
                {{/if}}
              {{/each}}
            </tbody>
          </table>
        </div>
      {{else}}
        <div class='ui message' data-test-no-bio-exam-result-data>
          Aucune donnée enregistrée.
        </div>
      {{/if}}
    {{/if}}
  </template>
}
